import { useGetParsedQuery, type UseGetParsedQueryOptions } from "@src/appV2/api/useGetParsedQuery";
import { environmentConfig } from "@src/appV2/environment";
import { type UseQueryResult } from "@tanstack/react-query";
import qs from "qs";
import { z } from "zod";

import { workerCredentialsResponseSchema } from "./schema";

export const WORKER_CREDENTIALS_ENDPOINT_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/worker-credentials`;

export const workerCredentialsQuerySchema = z.object({
  filter: z.object({
    workerIds: z.union([z.string(), z.array(z.string())]).optional(),
    workplaceId: z.string(),
  }),
  fields: z
    .object({
      workerCredential: z.array(z.string()).optional(),
    })
    .optional(),
  include: z.array(z.string()).optional(),
  page: z
    .object({
      size: z.number().optional(),
      cursor: z.string().optional(),
    })
    .optional(),
});

export type WorkerCredentialsQuery = z.input<typeof workerCredentialsQuerySchema>;
export type WorkerCredentialsResponse = z.output<typeof workerCredentialsResponseSchema>;
export type WorkerCredentialsResponseType = typeof workerCredentialsResponseSchema;

export function useGetWorkerCredentials(
  params: WorkerCredentialsQuery,
  options: UseGetParsedQueryOptions<WorkerCredentialsResponseType> = {}
): UseQueryResult<WorkerCredentialsResponse> {
  return useGetParsedQuery({
    url: WORKER_CREDENTIALS_ENDPOINT_URL,
    queryParams: params,
    queryParamsSchema: workerCredentialsQuerySchema,
    responseSchema: workerCredentialsResponseSchema,
    meta: {
      logErrorMessage: "Failed to fetch worker credentials",
      userErrorMessage: "Failed to fetch worker credentials",
    },
    // FIXME: Automatically disabled during ESLint config update
    paramsSerializer: (queryParams) => qs.stringify(queryParams, { arrayFormat: "comma" }),
    // default caching: 5 minutes unless caller overrides
    staleTime: options.staleTime ?? 5 * 60 * 1000,
    cacheTime: options.cacheTime ?? 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
}
